import type { IApiError } from '@apps/Application/widgets/AppSettingsForm'
import Textarea from '@common/forms/fields/Textarea'
import { useAppDispatch, useAppSelector, useTranslate } from '@common/hooks'
import Button from '@common/widgets/Button'
import ConfirmationModal from '@common/widgets/ConfirmationModal'
import { fetchApplicationRating, removeDeveloperResponse } from '@redux/slices/applicationRating'
import type { IAppReview } from '@redux/slices/applicationRating/applicationRating.types'
import { useState } from 'react'
import { FormProvider, useController, useForm } from 'react-hook-form'

import Review from '../Review'
import classNames from './styles.module.scss'
import submitHandler from './submitHandler'


interface IProps {
  editMode: boolean,
  initialValues: Record<string, any>,
  onHide: () => void,
  review: IAppReview,
}

export default function ModalReply({
  editMode,
  initialValues,
  onHide,
  review,
}: IProps): JSX.Element {
  const { translate } = useTranslate()
  const dispatch = useAppDispatch()

  const application = useAppSelector(store => store.application.data) as IApplication
  const token = useAppSelector(store => store.common.data.config?.token)

  const filterRating = useAppSelector(store => store.applicationRating.filters.rating) as number

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [serverError, setServerError] = useState<IApiError | null>(null)
  const [confirmationError, setConfirmationError] = useState<string | null>(null)

  const methods = useForm<Record<string, any>>({
    mode: 'onChange',
    defaultValues: { body: '', ...initialValues },
  })

  const { handleSubmit, formState: { isSubmitting } } = methods

  const { field: { ...bodyField } } = useController({
    name: 'body',
    control: methods.control,
  })

  const onClose = () => handleSubmit(async (fields) => {
    if(!token) { return }
    return await submitHandler({
      fields,
      appId: application.id,
      reviewId: review.id,
    },
    { deleteMode: false },
    {
      setError: (apiError: IApiError) => {
        setServerError(apiError)
      },
      clearErrors: () => {
        setServerError(null)
      },
      onSuccess: () => {
        void dispatch(fetchApplicationRating({
          id: application.id,
          refresh: true,
          rating: filterRating,
        }))
        onHide()
      },
    })
  })()

  return showConfirmationModal
    ? <ConfirmationModal
        onCancel={() => setShowConfirmationModal(false)}
        confirmationTitle="Confirm Reply Removal"
        confirmationDescription="Are you sure you want to remove this reply? This action cannot be undone"
        submitBtnText="Delete reply"
        confirmationError={confirmationError}
        handleAction={() => dispatch(removeDeveloperResponse({ appId: application.id, reviewId: review.id }))
          .then(() => {
            setConfirmationError(null)
            void dispatch(fetchApplicationRating({
              id: application.id,
              refresh: true,
              rating: filterRating,
            }))
            onHide()
          })
          .catch(_ => {
            setConfirmationError(translate('Something went wrong. Please try again later.'))
          })}
      />
    : (
        <section className={classNames.modalWrapper}>
          <Review {...review} className={classNames.review}/>
          <FormProvider {...methods}>
            <div className={classNames.formWrapper}>
              <header className={classNames.header} data-test="modal-response-title">
                {translate('App developer response')}
              </header>
              <Textarea
                {...bodyField}
                error={false}
                className={classNames.textarea}
                rows={3}
                maxLimit={2000}
                placeholder={translate('Replying publicly')}
                data-test="response-description"
              />
              <label className={classNames.label} data-test="modal-response-subtitle">
                {translate('This reply will be publicly visible')}
              </label>
              {serverError?.detail && <p data-test="response-error" className={classNames.serverError}>
                {Array.isArray(serverError.detail)
                  ? serverError.detail[0].msg
                  : serverError.detail.msg
                }
              </p>}
            </div>
            <footer className={classNames.footer}>
              {editMode && (
                <Button
                  variant="transparent"
                  className={classNames.removeBtn}
                  onClick={() => setShowConfirmationModal(true)}
                  data-test="remove-response-btn"
                >
                  {translate('Delete reply')}
                </Button>
              )}
              <Button
                className={classNames.submit}
                onClick={onClose}
                isLoading={isSubmitting}
                data-test="submit-response-btn"
                disabled={!((bodyField.value as string).length)}
              >
                {editMode ? translate('Save') : translate('Reply')}
              </Button>
            </footer>
          </FormProvider>
        </section>
      )
}
